import { useTranslation } from "react-i18next"

import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { cn } from "@/lib/utils"
import type { RejectionReasonOption } from "@/types"

/**
 * OKS-952 — the coded rejection-reason picker, shared by every surface that
 * can reject a candidate.
 *
 * # Why this component is CONTROLLED *and* emits `name=` attributes
 *
 * The three consuming surfaces do not agree on how they submit:
 *
 *   - `bulk-reject-confirmation.tsx` uses Inertia `<Form>` — it serialises the
 *     DOM at submit time, so the fields need real `name=` attributes.
 *   - `candidate-action-bar.tsx` and `candidate-review-actions.tsx` use
 *     `router.post` with a hand-built payload — they need the values in React
 *     state.
 *
 * Rather than fork the component, it does both: the caller owns the state
 * (`value` / `onValueChange` / `note` / `onNoteChange`) *and* every field
 * carries the wire name under `namePrefix`. A controlled value is still in the
 * DOM, so `<Form>` serialisation is unaffected.
 *
 * ⚠️ The two `router.post` surfaces are deliberately NOT converted to Inertia
 * `<Form>`. `router.post` is not a Crew form-rule violation (the rule bars
 * shadcn `Form`/`FormField`/`FormItem`/`FormMessage`, react-hook-form and zod
 * — none of which appear here), and converting them would rewrite modal state
 * their shipped Vitest specs assert.
 *
 * # Options are SERVER-DERIVED — never hardcode a code list here
 *
 * `codes` and `gate` come from the `rejection_reason_options` Inertia shared
 * prop, which the server derives from
 * `CandidateStatusEvent::SELECTABLE_REJECTION_REASON_CODES`. Two consequences
 * that are easy to undo by accident:
 *
 *   - `no_response` is already excluded server-side (it is system-applied by
 *     the auto-close job). This component does NOT filter and must not — a
 *     client-side filter would be a second definition of that rule.
 *   - A code added to the Ruby taxonomy appears here with no TypeScript
 *     change. Introducing a local list would break exactly that.
 *
 * ⚠️ `gate` selects the field label's VOICE only. It must never be used to
 * group or scope `codes`: `reviewed` and `pending_brand_validation` are
 * different gates whose code sets are disjoint by 6 and 2 entries, so grouping
 * by gate would offer 10 options where 6 are legal — and every gate-label
 * assertion would stay green while it happened.
 *
 * # `other` makes the note required
 *
 * Enforced here for latency only. The server is authoritative
 * (`Candidates::Transition` → `:reason_required_for_other`, re-asserted by
 * `CandidateStatusEvent#reason_code_matches_from_status`), so a bypassed
 * client check still fails closed. The error renders under the TEXTAREA, not
 * under the select — the offending field is the note.
 */
export interface RejectionReasonPickerProps {
  /**
   * The server-sent option set for this candidate's `from_status`, or
   * `undefined` when the status has no taxonomy entry (`new` cannot be
   * rejected) or the shared prop is absent. Renders nothing in that case —
   * never an empty dropdown.
   */
  options?: RejectionReasonOption
  /** Selected code. Empty string is the explicit "no reason given" choice. */
  value: string
  onValueChange: (next: string) => void
  /** Free-text note. Always available, required only when `value === "other"`. */
  note: string
  onNoteChange: (next: string) => void
  /**
   * Wire-format namespace — `"transition"` on the single-candidate surfaces,
   * `"bulk_transition"` on the batch one. Produces
   * `name="<prefix>[reason_code]"` and `name="<prefix>[reason]"`, matching the
   * nested strong-params signatures on both controllers.
   */
  namePrefix: "transition" | "bulk_transition"
  /** Prefix for `id` / `data-testid`, so several pickers can coexist on a page. */
  idPrefix: string
  disabled?: boolean
  /** Server-side field errors, keyed as the controllers emit them. */
  errors?: { reason_code?: string; reason?: string }
  /**
   * Set by the caller when it blocked a submit because `other` was chosen with
   * an empty note. A BOOLEAN, not a message: the picker owns the string, so
   * the three call sites cannot drift from each other or from the server's
   * wording (`candidates.reject.note_required_error` is a byte-identical
   * mirror of the backend key the server returns on the bypassed path).
   *
   * OKS-1012 — the flag is STICKY on the caller (reset only by its next
   * confirm), so the picker renders the error only while the refused condition
   * still holds: `noteMissing && isNoteMissing(value, note)`. Picking a coded
   * reason or typing a note clears the error, its `aria-invalid` and its id in
   * `aria-describedby` at once — otherwise the screen read « Note
   * (facultatif) » above an error demanding a note. Returning to `other` with
   * an empty note before the next confirm shows it again: that refusal still
   * applies.
   */
  noteMissing?: boolean
  /** Rendered under the select — the bulk surface's narrowing explanation. */
  helperSlot?: React.ReactNode
  /**
   * Overrides the note's derived `id`/`data-testid`.
   *
   * Exists so a surface that shipped BEFORE this component can adopt it
   * without renaming a testid its Vitest and Playwright specs already assert
   * (`candidate-detail-reject-reason`). Renaming those would have made this
   * ticket red LQE's E2E for a cosmetic reason. New surfaces should omit it
   * and take the derived default.
   */
  noteTestId?: string
  /** Rendered under the textarea — e.g. a surface's existing char counter. */
  noteFooter?: React.ReactNode
  /**
   * OKS-1012 — id of a surface-rendered element that DESCRIBES the note
   * (e.g. the visibility disclosure inside `noteFooter`). Joined into the
   * textarea's `aria-describedby` ahead of the note error, in DOM order.
   *
   * The id is supplied rather than derived here because the element lives in
   * the opaque `noteFooter` slot: the surface that renders the `<p>` owns its
   * id. Two constraints on the caller:
   * - pass an id that is rendered in EVERY state — a dangling idref is an a11y
   *   defect, not a no-op;
   * - point it at the description only, never at the counter or at a wrapper
   *   holding both (the counter would be read as part of the description).
   *
   * Omit it on a surface with no such element (the Brand Home review card):
   * its `aria-describedby` then stays error-only, exactly as before.
   */
  noteDescribedBy?: string
  className?: string
}

/** Hard cap, mirroring the shipped reject-reason textareas on every surface. */
const MAX_NOTE_LENGTH = 1000

/** The code that makes the free-text note mandatory. */
export const REASON_CODE_REQUIRING_NOTE = "other"

/**
 * Whether this (code, note) pair would be refused for a missing explanation.
 *
 * Exported so the three call sites share ONE definition of the rule instead of
 * each re-deriving `value === "other" && !note.trim()`. Latency only — the
 * server re-checks and is authoritative, so a caller that skips this still
 * fails closed with the same message.
 */
export function isNoteMissing(value: string, note: string): boolean {
  return value === REASON_CODE_REQUIRING_NOTE && note.trim().length === 0
}

/**
 * Whether this code makes the free-text note mandatory.
 *
 * Exported so the LABEL here and the surface-supplied `noteFooter` copy share
 * ONE definition. They previously disagreed: with `other` selected the label
 * read « Note (obligatoire) » while the footer helper still read
 * « Facultatif — … » (OKS-1003, PO 3.6 walk). The footer lives inside an
 * opaque `noteFooter` slot this component cannot introspect, so the rule is
 * exported rather than applied here — the surface owns its copy, this file
 * owns when the copy is wrong.
 */
export function isNoteRequired(value: string): boolean {
  return value === REASON_CODE_REQUIRING_NOTE
}

export function RejectionReasonPicker({
  options,
  value,
  onValueChange,
  note,
  onNoteChange,
  namePrefix,
  idPrefix,
  disabled = false,
  errors,
  noteMissing = false,
  helperSlot,
  noteTestId,
  noteFooter,
  noteDescribedBy,
  className,
}: RejectionReasonPickerProps) {
  const { t } = useTranslation()

  const noteRequired = isNoteRequired(value)
  // The client-side violation wins over a server error in the same slot: it
  // reflects what the user just did, whereas `errors.reason` may be left over
  // from the previous submit. It is shown only while it is still TRUE (see
  // `noteMissing`): a stale refusal must not outlive the state it refused.
  const clientNoteMissing = noteMissing && isNoteMissing(value, note)
  const noteError = clientNoteMissing
    ? t("candidates.reject.note_required_error")
    : (errors?.reason ?? null)

  const selectId = `${idPrefix}-reason-code`
  const noteId = noteTestId ?? `${idPrefix}-reason-note`
  const helpId = `${idPrefix}-reason-code-help`
  const noteErrorId = `${idPrefix}-reason-note-error`
  const codeErrorId = `${idPrefix}-reason-code-error`

  // A status with no taxonomy entry renders no select at all. Reachable only
  // defensively today (every status carrying a reject affordance has options),
  // but it keeps the component correct if a serializer ever widens the
  // affordance to a status the taxonomy does not cover — the note stays
  // available, so the surface never dead-ends.
  const hasOptions = Boolean(options && options.codes.length > 0)

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {hasOptions && options && (
        <div className="flex flex-col gap-2">
          <Label htmlFor={selectId}>
            {t(`candidates.reason_gate.${options.gate}.label`)}
          </Label>

          <Select
            value={value}
            disabled={disabled}
            onValueChange={(next) => onValueChange(next ?? "")}
          >
            <SelectTrigger
              id={selectId}
              data-testid={selectId}
              aria-describedby={helpId}
              aria-invalid={errors?.reason_code ? true : undefined}
              className="w-full"
            >
              {/*
                Base UI `SelectValue` renders the RAW value unless Root is given
                an `items` map — so the label is resolved explicitly here.
                The empty value is a real, re-selectable option (§3.2): a
                placeholder-only state cannot be un-picked once a code is chosen.
              */}
              <SelectValue>
                {value
                  ? t(`candidates.reason_code.${value}`)
                  : t("candidates.reject.reason_code.unspecified")}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                value=""
                data-testid={`${selectId}-option-unspecified`}
              >
                {t("candidates.reject.reason_code.unspecified")}
              </SelectItem>
              {options.codes.map((code) => (
                <SelectItem
                  key={code}
                  value={code}
                  data-testid={`${selectId}-option-${code}`}
                >
                  {t(`candidates.reason_code.${code}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/*
            Base UI emits its own aria-hidden native input from `Select name=`,
            but only when Root carries the name. An explicit mirror is used
            instead so the wire key is greppable in the DOM and asserted as a
            literal by the Vitest specs — the same posture the shipped
            `language-section.tsx` and `bulk-reject-confirmation.tsx` take.
          */}
          <input
            type="hidden"
            name={`${namePrefix}[reason_code]`}
            value={value}
          />

          <p id={helpId} className="text-muted-foreground text-xs">
            {t(`candidates.reason_gate.${options.gate}.help`)}
          </p>

          {helperSlot}

          {errors?.reason_code && (
            <p
              id={codeErrorId}
              data-testid={`${selectId}-error`}
              className="text-destructive text-xs"
            >
              {errors.reason_code}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        {/*
          The label text itself carries the required/optional state — colour is
          never the sole indicator (§3.7). `aria-required` follows the same
          flip so the change is announced, not merely shown.

          OKS-1012 — deliberately NO native `required` (docs/frontend/README.md
          rule 9). On a surface whose confirm is a real form submit (bulk), a
          native `required` made the browser block the submit with its own
          UNTRANSLATED bubble before the `isNoteMissing` guard could render the
          fr error. Required-ness is `aria-required` + the label + that guard,
          and the server re-checks. `bin/check-native-required` cannot see a
          `required={expr}` prop on a custom component, so do not rely on it to
          catch a regression here — the surface Vitest arms assert the absence.
        */}
        <Label htmlFor={noteId}>
          {noteRequired
            ? t("candidates.reject.note.required")
            : t("candidates.reject.note.optional")}
        </Label>
        <Textarea
          id={noteId}
          data-testid={noteId}
          name={`${namePrefix}[reason]`}
          rows={3}
          maxLength={MAX_NOTE_LENGTH}
          aria-required={noteRequired}
          aria-invalid={noteError ? true : undefined}
          aria-describedby={
            [noteDescribedBy, noteError ? noteErrorId : undefined]
              .filter(Boolean)
              .join(" ") || undefined
          }
          disabled={disabled}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
        />
        {noteFooter}
        {noteError && (
          <p
            id={noteErrorId}
            data-testid={`${noteId}-error`}
            className="text-destructive text-xs"
          >
            {noteError}
          </p>
        )}
      </div>
    </div>
  )
}
